import React from 'react';
import {
  History,
  UserPlus,
  Edit,
  Trash2,
  Building2,
  CheckCircle,
  XCircle,
  Copy,
  Radio,
  Lock,
  Shield,
  FileText,
  CreditCard,
  PenTool,
  Ban,
  AlertTriangle,
  Save,
  Mail,
  RefreshCw,
  Send,
  PlusCircle,
} from 'lucide-react';

export interface ActionConfig {
  icon: React.ComponentType<any>;
  label: string;
  colorClass: string;
}

export const ACTION_CONFIGS: Record<string, ActionConfig> = {
  // Actions communes
  'CREATE': { icon: CheckCircle, label: 'Création', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'UPDATE': { icon: Edit, label: 'Modification', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'DELETE': { icon: Trash2, label: 'Suppression', colorClass: 'tw-bg-red-100 tw-text-red-600' },
  'ACTIVATE': { icon: CheckCircle, label: 'Activation', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'DEACTIVATE': { icon: XCircle, label: 'Désactivation', colorClass: 'tw-bg-orange-100 tw-text-orange-600' },

  // Actions utilisateurs
  'ASSIGN_SOCIETY': { icon: Building2, label: 'Assignation société', colorClass: 'tw-bg-purple-100 tw-text-purple-600' },
  'UNASSIGN_SOCIETY': { icon: Building2, label: 'Désassignation société', colorClass: 'tw-bg-orange-100 tw-text-orange-600' },
  'SET_PRIMARY_SOCIETY': { icon: Building2, label: 'Société primaire', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'ANONYMIZE': { icon: Trash2, label: 'Anonymisation', colorClass: 'tw-bg-gray-100 tw-text-gray-600' },
  'PASSWORD_SET': { icon: Lock, label: 'Mot de passe défini', colorClass: 'tw-bg-yellow-100 tw-text-yellow-600' },
  'UPDATE_PERMISSIONS': { icon: Shield, label: 'Permissions modifiées', colorClass: 'tw-bg-purple-100 tw-text-purple-600' },
  'PROMOTE': { icon: UserPlus, label: 'Promotion admin', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'DEMOTE': { icon: Edit, label: 'Rétrogradation', colorClass: 'tw-bg-orange-100 tw-text-orange-600' },

  // Étapes de création (wizard)
  'INIT': { icon: FileText, label: 'Début création', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'VALIDATION': { icon: CheckCircle, label: 'Validation', colorClass: 'tw-bg-green-50 tw-text-green-600' },
  'CODE_GENERATION': { icon: FileText, label: 'Génération code', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'PARTS_VALIDATION': { icon: CheckCircle, label: 'Validation pièces', colorClass: 'tw-bg-green-50 tw-text-green-600' },
  'METIER_INTEGRATION': { icon: FileText, label: 'Intégration métier', colorClass: 'tw-bg-purple-50 tw-text-purple-600' },
  'DB_SAVE': { icon: FileText, label: 'Sauvegarde DB', colorClass: 'tw-bg-indigo-50 tw-text-indigo-600' },
  'PARTS_LINKING': { icon: FileText, label: 'Liaison pièces', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'COMPLETE': { icon: CheckCircle, label: 'Finalisation', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'ERROR': { icon: XCircle, label: 'Erreur', colorClass: 'tw-bg-red-100 tw-text-red-600' },
  'STEP_SUCCESS': { icon: CheckCircle, label: 'Étape réussie', colorClass: 'tw-bg-green-50 tw-text-green-600' },
  'STEP_ERROR': { icon: XCircle, label: 'Étape échouée', colorClass: 'tw-bg-red-50 tw-text-red-600' },
  'ROLLBACK': { icon: XCircle, label: 'Rollback', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Actions spécifiques garanties
  'DUPLICATE': { icon: Copy, label: 'Duplication', colorClass: 'tw-bg-purple-100 tw-text-purple-600' },
  'BROADCAST_UPDATE': { icon: Radio, label: 'Diffusion modifiée', colorClass: 'tw-bg-yellow-100 tw-text-yellow-600' },

  // Actions spécifiques API calls & métier
  'API_CALL_METIER_COMPANY': { icon: FileText, label: 'Appel API Métier', colorClass: 'tw-bg-purple-50 tw-text-purple-600' },
  'API_CALL_METIER_PARTNER': { icon: FileText, label: 'Appel API Partenaire', colorClass: 'tw-bg-purple-50 tw-text-purple-600' },
  'API_CALL_METIER_WARRANTY': { icon: FileText, label: 'Appel API Garantie', colorClass: 'tw-bg-purple-50 tw-text-purple-600' },
  'S3_UPLOAD': { icon: FileText, label: 'Upload S3', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'CONFIGURE_AS_HEAD': { icon: Building2, label: 'Configuration tête de groupe', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'UPDATE_INFOS': { icon: Edit, label: 'Modification informations', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'UPDATE_SAV': { icon: Edit, label: 'Modification SAV', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'UPDATE_CONFIG': { icon: Edit, label: 'Modification configuration', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'UPDATE_SAV_CONFIG': { icon: FileText, label: 'Modification config SAV', colorClass: 'tw-bg-orange-100 tw-text-orange-600' },
  'DELETE_SAV_CONFIG': { icon: Trash2, label: 'Réinitialisation config SAV', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Actions brouillon (draft)
  'DRAFT_CREATED': { icon: Save, label: 'Brouillon créé', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'DRAFT_UPDATED': { icon: Edit, label: 'Brouillon modifié', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'DRAFT_DELETED': { icon: Trash2, label: 'Brouillon supprimé', colorClass: 'tw-bg-gray-100 tw-text-gray-600' },

  // Échec de création
  'CREATION_FAILED': { icon: AlertTriangle, label: 'Création échouée', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Lifecycle - Paiement en ligne
  'PAYMENT_ONLINE_SUCCEEDED': { icon: CreditCard, label: 'Paiement en ligne réussi', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'PAYMENT_ONLINE_FAILED': { icon: CreditCard, label: 'Paiement en ligne échoué', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Lifecycle - Paiement mensuel
  'PAYMENT_MONTHLY_SUCCEEDED': { icon: CreditCard, label: 'Paiement mensuel réussi', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'PAYMENT_MONTHLY_FAILED': { icon: CreditCard, label: 'Paiement mensuel échoué', colorClass: 'tw-bg-orange-100 tw-text-orange-600' },
  'PAYMENT_MONTHLY_FAILED_TWICE': { icon: CreditCard, label: 'Paiement mensuel échoué (2x)', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Lifecycle - SetupIntent / Abonnement
  'SETUP_INTENT_SUBSCRIPTION_CREATED': { icon: CreditCard, label: 'Abonnement créé', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'SETUP_INTENT_PAYMENT_METHOD_UPDATED': { icon: CreditCard, label: 'Moyen de paiement enregistré', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },

  // Lifecycle - Signature
  'SIGNATURE_COMPLETED': { icon: PenTool, label: 'Signature électronique complétée', colorClass: 'tw-bg-green-100 tw-text-green-600' },

  // Lifecycle - Annulation
  'CONTRACT_CANCELLED': { icon: Ban, label: 'Contrat annulé', colorClass: 'tw-bg-red-100 tw-text-red-600' },

  // Lifecycle - Emails envoyés via webhooks
  'EMAIL_PAYMENT_MONTHLY_CONFIRMED': { icon: Mail, label: 'Email confirmation paiement mensuel', colorClass: 'tw-bg-green-50 tw-text-green-600' },
  'EMAIL_MONTHLY_INVOICE_SENT': { icon: Mail, label: 'Email facture mensuelle', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'EMAIL_PAYMENT_ONLINE_CONFIRMED': { icon: Mail, label: 'Email confirmation paiement carte', colorClass: 'tw-bg-green-50 tw-text-green-600' },
  'EMAIL_PAYMENT_FAILED_REMINDER': { icon: Mail, label: 'Email relance paiement', colorClass: 'tw-bg-orange-50 tw-text-orange-600' },
  'EMAIL_PAYMENT_METHOD_CONFIRMED': { icon: Mail, label: 'Email confirmation moyen de paiement', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'EMAIL_CONTRACT_CANCELLED': { icon: Mail, label: 'Email annulation contrat', colorClass: 'tw-bg-red-50 tw-text-red-600' },
  'EMAIL_ADMIN_ALERT': { icon: Mail, label: 'Alerte admin envoyée', colorClass: 'tw-bg-yellow-50 tw-text-yellow-600' },

  // Actions paiement (outil-pro)
  'PAYMENT_LINK_RESENT': { icon: Send, label: 'Lien de paiement renvoyé', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'PAYMENT_LINK_REGENERATED': { icon: CreditCard, label: 'Lien de paiement régénéré', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'UPDATE_PAYMENT_METHOD_INITIATED': { icon: RefreshCw, label: 'Mise à jour moyen de paiement initiée', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'PAYMENT_METHOD_UPDATE_LINK_SENT': { icon: Send, label: 'Lien mise à jour moyen de paiement envoyé', colorClass: 'tw-bg-blue-50 tw-text-blue-600' },
  'INVOICE_REGENERATED': { icon: RefreshCw, label: 'Facture échéancier régénérée', colorClass: 'tw-bg-amber-50 tw-text-amber-700' },
  'INVOICE_REGENERATED_BATCH': { icon: RefreshCw, label: 'Facture échéancier régénérée (batch)', colorClass: 'tw-bg-amber-50 tw-text-amber-700' },

  // Upload contrat signé
  'SIGNED_DOCUMENT_UPLOADED': { icon: FileText, label: 'Contrat signé uploadé', colorClass: 'tw-bg-green-100 tw-text-green-600' },

  // Conditions Générales
  'CREATE_VERSION': { icon: FileText, label: 'Nouvelle version CG', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'COPY_FROM_SOURCE': { icon: Copy, label: 'Personnalisation des CG', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'UPDATE_ARTICLE': { icon: Edit, label: 'Modification d\'article CG', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'CREATE_ARTICLE': { icon: PlusCircle, label: 'Ajout d\'article CG', colorClass: 'tw-bg-green-100 tw-text-green-600' },
  'DELETE_ARTICLE': { icon: Trash2, label: 'Suppression d\'article CG', colorClass: 'tw-bg-red-100 tw-text-red-600' },
  'REACTIVATE_VERSION': { icon: RefreshCw, label: 'Réactivation de version CG', colorClass: 'tw-bg-blue-100 tw-text-blue-600' },
  'UPDATE_METADATA': { icon: Edit, label: 'Modification métadonnées CG', colorClass: 'tw-bg-indigo-100 tw-text-indigo-600' },
  'DELETE_CG': { icon: Trash2, label: 'Suppression des CG', colorClass: 'tw-bg-red-100 tw-text-red-600' },
};

export const getActionConfig = (action: string): ActionConfig => {
  return ACTION_CONFIGS[action] || {
    icon: History,
    label: action,
    colorClass: 'tw-bg-gray-100 tw-text-gray-600',
  };
};
